import {
  useLoaderData,
  useRouteLoaderData,
  useSearchParams,
} from "react-router-dom";
import { Organization } from "../../../services/organization/organizationModel";
import { useTranslation } from "react-i18next";
import ProPaymentMethodForm from "./ProPaymentMethodForm";
import DisplaySubscription from "../../../components/DisplaySubscription";
import useReload from "../../../hooks/useReload";
import { subscriptionService } from "../../../services/subscription/subscriptionService";
import SubscribeDialog from "./SubscribeDialog";
import { useEffect, useMemo, useRef, useState } from "react";
import BuyCapsulesCreditsDialog from "./BuyCapsulesCreditsDialog";
import { toastsWithIntl } from "../../../services/toastService";
import { User } from "../../../services/user/userModel";
import { Capsule } from "../../../services/capsule/capsuleModel";

const { getOrganizationPortalLink } = subscriptionService();
const { toastSuccess } = toastsWithIntl(["organization"]);

// The subscription is activated by the Stripe webhook, which can lag a few
// seconds behind the Checkout redirect: poll the loader until it shows up.
const CHECKOUT_PENDING_RELOAD_INTERVAL_MS = 3000;
const CHECKOUT_PENDING_MAX_RELOADS = 10;

const SubscriptionPage = () => {
  const { loggedUser } = useRouteLoaderData("logged-user") as {
    loggedUser: User;
  };
  const { organization } = useRouteLoaderData("organization") as {
    organization: Organization;
  };
  const { capsules } = useLoaderData() as {
    capsules: Capsule[];
  };
  const { t } = useTranslation(["organization"]);
  const reload = useReload();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
  const [showBuyCreditsDialog, setShowBuyCreditsDialog] = useState(false);
  const [awaitingActivation, setAwaitingActivation] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const remainingReloadsRef = useRef(CHECKOUT_PENDING_MAX_RELOADS);

  const invitation = useMemo(() => {
    return loggedUser.invitations?.[0] ?? null;
  }, [loggedUser]);

  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState !== "hidden") reload();
    }

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [reload]);

  useEffect(() => {
    const checkoutResult = searchParams.get("checkout");
    if (!checkoutResult) return;
    if (checkoutResult === "success") {
      toastSuccess("organization:subscription.checkout.SUCCESS", {
        id: "checkout-success",
      });
      setAwaitingActivation(true);
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const awaitingSubscriptionActivation =
    awaitingActivation && !organization.subscriptions?.[0];

  useEffect(() => {
    if (!awaitingSubscriptionActivation) return;

    const interval = setInterval(() => {
      if (remainingReloadsRef.current <= 0) {
        clearInterval(interval);
        return;
      }
      remainingReloadsRef.current -= 1;
      reload();
    }, CHECKOUT_PENDING_RELOAD_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [awaitingSubscriptionActivation, reload]);

  return (
    <div className="layout_content --bg--organization-dashboard">
      <div className="ui-page">
        <div className="ui-ph">
          <div className="ui-ph_t">
            <h1>{t("organization:subscription.PAGE_TITLE")}</h1>
          </div>
        </div>

        <div className="ui-grid --g2 admin-sub-page">
          <div className="admin-stack">
            <section className="ui-card --pad">
              <h2 className="admin-eyebrow">
                {t("organization:subscription.SUBSCRIPTION")}
              </h2>
              {organization.subscriptions?.[0] ? (
                <DisplaySubscription
                  subscription={organization.subscriptions[0]}
                  cta={
                    <div className="admin-sub_cta">
                      <button
                        className="btn--2"
                        disabled={loadingPortal}
                        onClick={() => {
                          setLoadingPortal(true);
                          getOrganizationPortalLink(organization.id)
                            .then(({ url }) => {
                              window.location.href = url;
                            })
                            .catch(() => undefined)
                            .finally(() => setLoadingPortal(false));
                        }}
                      >
                        {loadingPortal
                          ? "..."
                          : t("organization:subscription.MANAGE_SUBSCRIPTION")}
                      </button>
                    </div>
                  }
                />
              ) : awaitingSubscriptionActivation ? (
                <div className="ui-banner">
                  {t("organization:subscription.checkout.PENDING_ACTIVATION")}
                </div>
              ) : (
                <div className="admin-sub_cta">
                  <button
                    className="btn--1"
                    onClick={() => setShowSubscribeDialog(true)}
                  >
                    {t("organization:subscription.NEW_SUBSCRIPTION")}
                  </button>
                </div>
              )}
            </section>
            {invitation && (
              <section className="ui-card --pad">
                <h2 className="admin-eyebrow">
                  {t("organization:subscription.INVITATION")}
                </h2>
                {(invitation.organization?.subscriptions?.length ?? 0) > 0 ? (
                  <div className="ui-banner --ok">
                    {t(
                      "organization:subscription.INVITATION_VALID_SUB_DESCRIPTION",
                      {
                        replace: {
                          pro: invitation.organization?.legalName,
                        },
                      },
                    )}
                  </div>
                ) : (
                  <div className="ui-banner --warn">
                    {t(
                      "organization:subscription.INVITATION_EXPIRED_SUB_DESCRIPTION",
                      {
                        replace: {
                          pro: invitation.organization?.legalName,
                        },
                      },
                    )}
                  </div>
                )}
              </section>
            )}
          </div>
          <div className="admin-stack">
            <section className="ui-card --pad">
              <div className="admin-card-head">
                <h2 className="ui-st3">
                  {t("organization:subscription.CAPSULES_CREDITS")}
                </h2>
                <button
                  className="btn--1 --btn--s"
                  onClick={() => setShowBuyCreditsDialog(true)}
                >
                  {t("organization:subscription.capsules-credits.BUY")}
                </button>
              </div>
              <div className="admin-credits">
                <span className="admin-credits_n">
                  {organization.maxCapsulesCount - capsules.length}
                </span>
                <span className="ui-muted">
                  {t("organization:subscription.capsules-credits.CREDITS_LEFT")}
                </span>
              </div>
            </section>
            {!!organization.stripeCustomer && (
              <section className="ui-card --pad">
                <h2 className="ui-st3">
                  {t("organization:subscription.PAYMENT_METHOD")}
                </h2>
                <ProPaymentMethodForm organization={organization} />
              </section>
            )}
          </div>
        </div>
      </div>

      {showSubscribeDialog && (
        <SubscribeDialog
          organization={organization}
          onClose={() => setShowSubscribeDialog(false)}
        />
      )}
      {showBuyCreditsDialog && (
        <BuyCapsulesCreditsDialog
          onClose={() => setShowBuyCreditsDialog(false)}
          organization={organization}
        />
      )}
    </div>
  );
};

export default SubscriptionPage;
